/**
 * StackableButton —— BuildingStackableBtn 的 React 抽象。
 *
 * 覆盖标准建筑按钮的 sell 链、togglable +/- 下拉、on/off 开关、自动化标记、
 * 以及 bldEnabled/bldlackResConvert/font-size:90%/small-text 等 domNode 钩子。
 *
 * 本组件不绑定具体 controller 构造器；调用侧通过 hook 传入快照、controller、
 * modelRef 与 tooltip 回调。
 */
import { useCallback, useState, type MouseEvent, type ReactNode } from "react";
import { getGame } from "../bridge/GameBridge";
import type { GamePage } from "../bridge/GameBridge";
import { t } from "../bridge/i18n";
import { GameButton } from "./GameButton";
import type { GameButtonLinkModel } from "./GameButton";

export interface StackableLinkSnapshot {
  title: string;
  cssClass?: string;
  tooltip?: string;
  handler: (event: MouseEvent) => void;
}

export interface StackableButtonSnapshot {
  name: string;
  description: string;
  visible: boolean;
  enabled: boolean;
  resourceIsLimited: string | boolean;
  /** metadata.val / on */
  val?: number;
  on?: number;
  /** metadata.name（small-text 规则用） */
  metaName?: string;
  /** metadata.lackResConvert（bldlackResConvert 态） */
  lackResConvert?: boolean;
  togglable?: boolean;
  hasSellLink?: boolean;
  showSellLink?: boolean;
  togglableOnOffLink?: StackableLinkSnapshot;
  toggleAutomationLink?: StackableLinkSnapshot;
}

export interface StackableButtonController {
  buyItem?(model: unknown, event?: MouseEvent): { itemBought: boolean; reason: string } | void;
  off?(model: unknown, amt?: number): void;
  offAll?(model: unknown): void;
  on?(model: unknown, amt?: number): void;
  onAll?(model: unknown): void;
  sell?(event: MouseEvent, model: unknown): void;
}

export interface StackableButtonProps {
  snap: StackableButtonSnapshot;
  controller: StackableButtonController | null;
  /** 获取当前 model（modelRef.current），用于点击/link/hover */
  getModel: () => unknown;
  getTooltip: () => string;
  /** 是否在购买后调用 game.render()（BuildingStackableBtn.onClick 语义） */
  renderAfterBuy?: boolean;
  /** hover 时 setSelectedObject 的对象；默认取 getModel() */
  getSelectedObject?: () => unknown;
  /** 点击主按钮的额外回调（在 buyItem + render 之后） */
  onClick?: (event: MouseEvent) => void;
}

interface LinkListItem {
  id: string;
  title: string;
  handler: () => void;
}

function LinkListDropdown({
  items,
  rootEnabled,
}: {
  items: LinkListItem[];
  rootEnabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (!items.length) {
    return null;
  }
  const [root, ...rest] = items;
  const onLinkClick = (handler: () => void) => (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    handler();
  };
  return (
    <div
      style={{ float: "right" }}
      onMouseOver={() => setOpen(true)}
      onMouseOut={() => setOpen(false)}
    >
      <a
        href="#"
        className={root.id + "Link" + (rootEnabled ? " enabled" : "")}
        style={{ display: "block", float: "right" }}
        title={root.title}
        onClick={onLinkClick(root.handler)}
        dangerouslySetInnerHTML={{ __html: root.title }}
      />
      <div
        className="linkContent"
        style={{
          display: open ? "block" : "none",
          position: "absolute",
          float: "right",
          marginTop: "35px",
          zIndex: 100,
        }}
      >
        {rest.map((item) => (
          <a
            key={item.id}
            href="#"
            className="dropdown-link"
            style={{ display: "block" }}
            title={item.title}
            onClick={onLinkClick(item.handler)}
            dangerouslySetInnerHTML={{ __html: item.title }}
          />
        ))}
      </div>
    </div>
  );
}

export function StackableButton({
  snap,
  controller,
  getModel,
  getTooltip,
  renderAfterBuy = true,
  getSelectedObject,
  onClick,
}: StackableButtonProps) {
  const handleClick = useCallback(
    (event: MouseEvent) => {
      controller?.buyItem?.(getModel(), event);
      if (renderAfterBuy) {
        const game = getGame() as (GamePage & { render?: () => void }) | null;
        game?.render?.();
      }
      onClick?.(event);
    },
    [controller, getModel, renderAfterBuy, onClick],
  );

  if (!snap.visible || !controller) {
    return null;
  }

  const val = snap.val ?? 0;
  const on = snap.on ?? 0;
  const metaName = snap.metaName ?? "";
  const hasToggleLinks = snap.togglable || !!snap.togglableOnOffLink;

  const domClassName =
    (snap.hasSellLink ? " hasSellLink" : "") +
    (snap.hasSellLink && (val > 9 || metaName.length > 10) ? " small-text" : "") +
    (hasToggleLinks && val > 0 && on > 0
      ? snap.lackResConvert
        ? " bldlackResConvert"
        : " bldEnabled"
      : "");

  const domStyle = val > 9 ? { fontSize: "90%" } : undefined;

  const links: GameButtonLinkModel[] = [];

  if (snap.hasSellLink) {
    links.push({
      title: t("btn.sell.minor"),
      visible: snap.showSellLink,
      handler: (event) => controller.sell?.(event, getModel()),
    });
  }

  if (snap.togglableOnOffLink) {
    const link = snap.togglableOnOffLink;
    links.push({
      title: link.title,
      cssClass: link.cssClass,
      tooltip: link.tooltip,
      handler: (event) => link.handler(event),
    });
  }

  if (snap.toggleAutomationLink) {
    const link = snap.toggleAutomationLink;
    links.push({
      title: link.title,
      cssClass: link.cssClass,
      tooltip: link.tooltip,
      handler: (event) => link.handler(event),
    });
  }

  const children: ReactNode[] = [];

  if (snap.togglable) {
    children.push(
      <LinkListDropdown
        key="remove"
        items={[
          { id: "off1", title: "-", handler: () => controller.off?.(getModel()) },
          { id: "off25", title: "-25", handler: () => controller.off?.(getModel(), 25) },
          {
            id: "offAll",
            title: "-" + t("btn.all.minor"),
            handler: () => controller.offAll?.(getModel()),
          },
        ]}
      />,
      <LinkListDropdown
        key="add"
        rootEnabled={val > 0 && on < val}
        items={[
          { id: "add1", title: "+", handler: () => controller.on?.(getModel()) },
          { id: "add25", title: "+25", handler: () => controller.on?.(getModel(), 25) },
          {
            id: "add",
            title: "+" + t("btn.all.minor"),
            handler: () => controller.onAll?.(getModel()),
          },
        ]}
      />,
    );
  }

  const selectedObject = getSelectedObject ? getSelectedObject : () => getModel();

  return (
    <GameButton
      name={snap.name}
      description={snap.description}
      enabled={snap.enabled}
      resourceIsLimited={snap.resourceIsLimited}
      getTooltip={getTooltip}
      onClick={handleClick}
      getSelectedObject={selectedObject}
      domClassName={domClassName}
      domStyle={domStyle}
      links={links}
    >
      {children}
    </GameButton>
  );
}
